import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Package,
  User,
  Calendar,
  AlertTriangle,
  CheckCircle,
  Truck,
  XCircle,
  Clock,
} from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Badge } from '@/app/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Skeleton } from '@/app/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/app/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/app/components/ui/dialog';
import { Textarea } from '@/app/components/ui/textarea';
import { AssetPicker } from '@/app/components/inventory/asset-picker';
import { KitPicker } from '@/app/components/inventory/kit-picker';
import { Label } from '@/app/components/ui/label';
import { toast } from '@/app/components/ui/simple-toaster';
import {
  useGearRequest,
  useFulfillGearRequest,
  useMarkGearRequestInTransit,
  useCancelGearRequest,
} from '@/hooks/use-gear-requests';
import { usePermissions } from '@/hooks/use-permissions';
import { useOrgTimeZone, useOrgToday } from '@/hooks/use-settings';
import { PageBreadcrumb } from '@/app/components/page-breadcrumb';
import { formatDateOnly, formatDateTimeInTimeZone } from '@/lib/date-utils';
import type { GearRequestStatus, GearRequestPriority, GearRequestEvent } from '@/lib/types';

const statusColors: Record<GearRequestStatus, string> = {
  pending_confirmation: 'bg-amber-100 text-amber-800',
  requested: 'bg-yellow-100 text-yellow-800',
  fulfilled: 'bg-blue-100 text-blue-800',
  in_transit: 'bg-purple-100 text-purple-800',
  partially_received: 'bg-orange-100 text-orange-800',
  received: 'bg-green-100 text-green-800',
  returned: 'bg-teal-100 text-teal-800',
  cancelled: 'bg-gray-100 text-gray-800',
};

const statusLabels: Record<GearRequestStatus, string> = {
  pending_confirmation: 'Pending Confirmation',
  requested: 'Requested',
  fulfilled: 'Fulfilled',
  in_transit: 'In Transit',
  partially_received: 'Partially Received',
  received: 'Received',
  returned: 'Returned to Inventory',
  cancelled: 'Cancelled',
};

const priorityColors: Record<GearRequestPriority, string> = {
  low: 'bg-gray-100 text-gray-700',
  normal: 'bg-blue-50 text-blue-700',
  high: 'bg-orange-100 text-orange-700',
  urgent: 'bg-red-100 text-red-700',
};

export function GearRequestDetailPage() {
  const orgTimeZone = useOrgTimeZone();
  const orgToday = useOrgToday();
  const { id } = useParams<{ id: string }>();
  const requestId = parseInt(id || '0', 10);

  const { data: request, isLoading, isError } = useGearRequest(requestId);
  const { canFulfillGearRequests } = usePermissions();

  const fulfillMutation = useFulfillGearRequest();
  const markInTransitMutation = useMarkGearRequestInTransit();
  const cancelMutation = useCancelGearRequest();

  const [fulfillDialogOpen, setFulfillDialogOpen] = useState(false);
  const [inTransitDialogOpen, setInTransitDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [transitionNotes, setTransitionNotes] = useState('');
  const [lineAssets, setLineAssets] = useState<Record<string, number>>({});
  const [lineKits, setLineKits] = useState<Record<string, number>>({});



  const handleFulfill = async () => {
    try {
      await fulfillMutation.mutateAsync({
        id: requestId,
        notes: transitionNotes,
      });
      toast.success('Gear request marked as fulfilled');
      setFulfillDialogOpen(false);
      setTransitionNotes('');
    } catch {
      toast.error('Failed to fulfill gear request');
    }
  };

  const handleMarkInTransit = async () => {
    try {
      await markInTransitMutation.mutateAsync({
        id: requestId,
        notes: transitionNotes,
        line_assets: lineAssets,
        line_kits: lineKits,
      });
      toast.success('Gear request marked as in transit');
      setInTransitDialogOpen(false);
      setTransitionNotes('');
      setLineAssets({});
      setLineKits({});
    } catch {
      toast.error('Failed to mark gear request as in transit');
    }
  };

  const handleCancel = async () => {
    try {
      await cancelMutation.mutateAsync({
        id: requestId,
        notes: transitionNotes,
      });
      toast.success('Gear request cancelled');
      setCancelDialogOpen(false);
      setTransitionNotes('');
    } catch {
      toast.error('Failed to cancel gear request');
    }
  };

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError || !request) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="text-center py-12">
          <AlertTriangle className="h-12 w-12 mx-auto text-red-500 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900">
            Gear request not found
          </h2>
          <p className="text-gray-600 mt-2">
            The gear request you&apos;re looking for doesn&apos;t exist or you
            don&apos;t have access.
          </p>
          <Button asChild className="mt-4">
            <Link to="/gear-requests">Back to Gear Requests</Link>
          </Button>
        </div>
      </div>
    );
  }

  const overdue =
    request.needed_by &&
    request.needed_by.slice(0, 10) < orgToday &&
    request.status !== 'received' &&
    request.status !== 'cancelled';

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <PageBreadcrumb
        items={[
          { label: 'Gear Requests', href: '/gear-requests' },
          { label: `Request #${request.id}` },
        ]}
      />

      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/gear-requests">
            <ArrowLeft className="h-5 w-5" />
          </Link>
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
            Gear Request #{request.id}
          </h1>
          <p className="text-gray-600">
            Created {formatDateTimeInTimeZone(request.created_at, orgTimeZone)}
          </p>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <Package className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Status</p>
                <Badge className={statusColors[request.status]}>
                  {statusLabels[request.status]}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Priority</p>
                <Badge className={priorityColors[request.priority]}>
                  {request.priority}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <User className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Requester</p>
                <p className="text-lg font-semibold">
                  {request.requested_by_username || 'Unknown'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <Calendar className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Needed By</p>
                <p
                  className={`text-lg font-semibold ${overdue ? 'text-red-600' : ''}`}
                >
                  {request.needed_by
                    ? formatDateOnly(request.needed_by)
                    : 'Not set'}
                </p>
                {overdue && <Badge variant="destructive">Overdue</Badge>}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Manager actions — only shown for users with write access */}
      {canFulfillGearRequests && (
        <Card>
          <CardHeader>
            <CardTitle>Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {request.status === 'requested' && (
                <>
                  <Button onClick={() => setFulfillDialogOpen(true)}>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Mark Fulfilled
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => setCancelDialogOpen(true)}
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Cancel Request
                  </Button>
                </>
              )}
              {request.status === 'fulfilled' && (
                <>
                  <Button onClick={() => setInTransitDialogOpen(true)}>
                    <Truck className="h-4 w-4 mr-2" />
                    Mark In Transit / Sent
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => setCancelDialogOpen(true)}
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Cancel Request
                  </Button>
                </>
              )}
              {(request.status === 'in_transit' ||
                request.status === 'partially_received') && (
                <Button asChild>
                  <Link to={`/gear-requests/${request.id}/receive`}>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Confirm Receipt
                  </Link>
                </Button>
              )}
              {(request.status === 'received' ||
                request.status === 'cancelled') && (
                <p className="text-sm text-gray-500 italic">
                  No actions available for this status.
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Notes */}
      {request.notes && (
        <Card>
          <CardHeader>
            <CardTitle>Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 whitespace-pre-wrap">{request.notes}</p>
          </CardContent>
        </Card>
      )}

      {/* Request lines */}
      <Card>
        <CardHeader>
          <CardTitle>
            Request Lines ({request.lines.length}{' '}
            {request.lines.length === 1 ? 'item' : 'items'})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {request.lines.length === 0 ? (
            <div className="text-center py-8">
              <Package className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600">No line items.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Description</TableHead>
                  <TableHead className="w-24 text-right">Qty</TableHead>
                  <TableHead>Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {request.lines.map((line) => (
                  <TableRow key={line.id}>
                    <TableCell className="font-medium">
                      {line.description}
                    </TableCell>
                    <TableCell className="text-right">{line.quantity}</TableCell>
                    <TableCell className="text-gray-500">
                      {line.notes || '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Transition history */}
      <Card>
        <CardHeader>
          <CardTitle>Transition History</CardTitle>
        </CardHeader>
        <CardContent>
          {request.events.length === 0 ? (
            <p className="text-gray-500 text-center py-4">
              No transition events recorded.
            </p>
          ) : (
            <div className="space-y-4">
              {request.events.map((event) => (
                <TransitionEventRow key={event.id} event={event} timeZone={orgTimeZone} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Fulfill dialog */}
      <Dialog open={fulfillDialogOpen} onOpenChange={(open) => { setFulfillDialogOpen(open); if (!open) setTransitionNotes(''); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark as Fulfilled</DialogTitle>
            <DialogDescription>
              Confirm that the requested gear has been gathered and is ready for
              shipment or pickup.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="fulfill-notes">Notes (optional)</Label>
              <Textarea
                id="fulfill-notes"
                value={transitionNotes}
                onChange={(e) => setTransitionNotes(e.target.value)}
                placeholder="Add any notes about the fulfillment..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setFulfillDialogOpen(false);
                setTransitionNotes('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleFulfill}
              disabled={fulfillMutation.isPending}
            >
              {fulfillMutation.isPending ? 'Fulfilling...' : 'Mark Fulfilled'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* In Transit dialog */}
      <Dialog
        open={inTransitDialogOpen}
        onOpenChange={(open) => {
          setInTransitDialogOpen(open);
          if (!open) {
            setTransitionNotes('');
            setLineAssets({});
            setLineKits({});
          }
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Mark as In Transit / Sent</DialogTitle>
            <DialogDescription>
              Optionally select specific assets or kits for each line before
              shipping. Selected items will be deployed automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {request.lines.length > 0 && (
              <div className="space-y-3">
                <Label>Assign inventory to lines</Label>
                {request.lines.map((line) => (
                  <div
                    key={line.id}
                    className="border rounded-md p-3 space-y-2"
                  >
                    <p className="text-sm font-medium">
                      {line.description}{' '}
                      <span className="text-gray-500">× {line.quantity}</span>
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs text-gray-500">Asset</Label>
                        <AssetPicker
                          value={lineAssets[String(line.id)] || null}
                          onChange={(val) =>
                            setLineAssets((prev) => {
                              const next = { ...prev };
                              if (val === 'none') {
                                delete next[String(line.id)];
                              } else {
                                next[String(line.id)] = Number(val);
                              }
                              return next;
                            })
                          }
                          status="available"
                          allowNone
                          noneLabel="None"
                          noneValue="none"
                          placeholder="None"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Kit</Label>
                        <KitPicker
                          value={lineKits[String(line.id)] || null}
                          onChange={(val) =>
                            setLineKits((prev) => {
                              const next = { ...prev };
                              if (val === 'none') {
                                delete next[String(line.id)];
                              } else {
                                next[String(line.id)] = Number(val);
                              }
                              return next;
                            })
                          }
                          status="available"
                          allowNone
                          noneLabel="None"
                          noneValue="none"
                          placeholder="None"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="transit-notes">Notes (optional)</Label>
              <Textarea
                id="transit-notes"
                value={transitionNotes}
                onChange={(e) => setTransitionNotes(e.target.value)}
                placeholder="Tracking number, carrier, etc..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setInTransitDialogOpen(false);
                setTransitionNotes('');
                setLineAssets({});
                setLineKits({});
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleMarkInTransit}
              disabled={markInTransitMutation.isPending}
            >
              {markInTransitMutation.isPending
                ? 'Updating...'
                : 'Mark In Transit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={(open) => { setCancelDialogOpen(open); if (!open) setTransitionNotes(''); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Gear Request</DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel this gear request? This action
              cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="cancel-notes">Reason (optional)</Label>
              <Textarea
                id="cancel-notes"
                value={transitionNotes}
                onChange={(e) => setTransitionNotes(e.target.value)}
                placeholder="Reason for cancellation..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setCancelDialogOpen(false);
                setTransitionNotes('');
              }}
            >
              Go Back
            </Button>
            <Button
              variant="destructive"
              onClick={handleCancel}
              disabled={cancelMutation.isPending}
            >
              {cancelMutation.isPending
                ? 'Cancelling...'
                : 'Cancel Request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface TransitionEventRowProps {
  event: GearRequestEvent;
  timeZone: string;
}

function TransitionEventRow({ event, timeZone }: TransitionEventRowProps) {
  const fromLabel =
    event.from_status
      ? statusLabels[event.from_status as GearRequestStatus] || event.from_status
      : 'Created';
  const toLabel =
    statusLabels[event.to_status as GearRequestStatus] || event.to_status;

  return (
    <div className="flex items-start gap-3 border-l-2 border-gray-200 pl-4 py-1">
      <div className="p-1.5 bg-gray-100 rounded-full mt-0.5">
        <Clock className="h-3.5 w-3.5 text-gray-500" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900">
          {fromLabel} &rarr; {toLabel}
        </p>
        {event.actor_username && (
          <p className="text-sm text-gray-500">by {event.actor_username}</p>
        )}
        {event.notes && (
          <p className="text-sm text-gray-600 mt-1">{event.notes}</p>
        )}
        <p className="text-xs text-gray-400 mt-1">
          {formatDateTimeInTimeZone(event.created_at, timeZone)}
        </p>
      </div>
    </div>
  );
}
